import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import { frotistaAPI } from "../../../servicos/frotista";
import { normalizeJobUpdate, useLoteJobSubscription } from "../../Emplacar/hooks/useLoteJobSubscription";
import {
  consultaTiposToArray,
  normalizarConsultaTipos,
} from "./consultaTiposUtils";
import DebitosLotePanel from "./DebitosLotePanel";

const STORAGE_KEY = "debitoLoteState";

const estadoInicial = {
  status: "idle",
  parametros: null,
  resultado: { veiculos: [] },
  erro: null,
  recebidoEm: null,
  jobResumo: null,
  itens: [],
  iniciadoEm: null,
};

const DebitosLoteContext = createContext(null);

const normalizarVeiculos = (payload) => {
  if (!payload) {
    return [];
  }
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload.veiculos)) {
    return payload.veiculos;
  }
  if (Array.isArray(payload.vehicles)) {
    return payload.vehicles;
  }
  if (Array.isArray(payload.resultado)) {
    return payload.resultado;
  }
  if (Array.isArray(payload.data)) {
    return payload.data;
  }
  return [];
};

const normalizarResultado = (payload) => ({
  veiculos: normalizarVeiculos(payload),
  bruto: payload ?? null,
});

const calcularResumo = (resultado) => {
  const veiculos = normalizarVeiculos(resultado);
  const totalVeiculos = veiculos.length;
  const totalDebitos = veiculos.reduce((acc, veiculo) => {
    const debitos = Array.isArray(veiculo.debitos) ? veiculo.debitos.length : 0;
    return acc + debitos;
  }, 0);
  const totalMultas = veiculos.reduce((acc, veiculo) => {
    const debitos = Array.isArray(veiculo.debitos) ? veiculo.debitos : [];
    const multas = debitos.flatMap((debito) => (Array.isArray(debito.multas) ? debito.multas : []));
    return acc + multas.length;
  }, 0);

  return { totalVeiculos, totalDebitos, totalMultas };
};

const ensureString = (valor) => {
  if (valor === undefined || valor === null) {
    return "";
  }
  if (typeof valor === "string") {
    return valor;
  }
  return String(valor);
};

const normalizarRenavam = (valor) => ensureString(valor).trim();

const normalizarVeiculoSelecionado = (entrada) => {
  if (entrada === undefined || entrada === null) {
    return null;
  }
  if (typeof entrada === "string") {
    const renavam = normalizarRenavam(entrada);
    return renavam ? { renavam, placa: "" } : null;
  }
  const renavam = normalizarRenavam(
    entrada.renavam ||
      entrada.RENAVAM ||
      entrada.renavamAtual ||
      entrada.value ||
      "",
  );
  if (!renavam) {
    return null;
  }
  const placa = ensureString(
    entrada.placa ||
      entrada.placaAtual ||
      entrada.plate ||
      entrada.placaVeiculo ||
      "",
  )
    .toUpperCase()
    .trim();
  return { renavam, placa };
};

const extrairVeiculosSelecionados = (entrada) => {
  if (!entrada) {
    return [];
  }
  const resultado = [];
  const adicionar = (item) => {
    const normalizado = normalizarVeiculoSelecionado(item);
    if (normalizado) {
      resultado.push(normalizado);
    }
  };
  const possuiVeiculos = Array.isArray(entrada?.veiculos) && entrada.veiculos.length > 0;
  if (possuiVeiculos) {
    entrada.veiculos.forEach(adicionar);
  } else if (Array.isArray(entrada?.renavams)) {
    entrada.renavams.forEach((valor) => adicionar({ renavam: valor }));
  }
  if (Array.isArray(entrada)) {
    entrada.forEach(adicionar);
  }
  return resultado;
};

const parseResponseBody = (valor) => {
  if (!valor) {
    return null;
  }
  if (typeof valor === "string") {
    const texto = valor.trim();
    if (!texto) {
      return null;
    }
    try {
      return JSON.parse(texto);
    } catch (_erro) {
      return null;
    }
  }
  return valor;
};

const extrairVeiculosDoResponse = (payload) => {
  if (!payload) {
    return [];
  }

  if (Array.isArray(payload)) {
    return payload;
  }

  const candidatos = [
    payload.veiculos,
    payload.veiculo,
    payload.resultado,
    payload.data,
    payload.body,
  ]
    .filter((item) => Array.isArray(item))
    .flat();

  if (candidatos.length) {
    return candidatos;
  }

  if (payload.resultado?.veiculos && Array.isArray(payload.resultado.veiculos)) {
    return payload.resultado.veiculos;
  }
  if (payload.data?.veiculos && Array.isArray(payload.data.veiculos)) {
    return payload.data.veiculos;
  }

  if (payload.veiculo && typeof payload.veiculo === "object") {
    return [payload.veiculo];
  }

  return [];
};

const extrairMensagemErro = (payload) =>
  ensureString(
    payload?.erro ||
      payload?.error ||
      payload?.mensagem ||
      payload?.message ||
      payload?.descricao ||
      payload?.description ||
      "",
  );

const extrairVeiculoPrincipal = (payload, fallbackRenavam) => {
  const resposta = parseResponseBody(payload);
  const veiculos = extrairVeiculosDoResponse(resposta);
  if (veiculos.length > 0) {
    const veiculo = { ...veiculos[0] };
    if (!veiculo.renavam && fallbackRenavam) {
      veiculo.renavam = fallbackRenavam;
    }
    return veiculo;
  }

  if (resposta && typeof resposta === "object") {
    const { veiculo } = resposta;
    if (veiculo && typeof veiculo === "object") {
      return {
        ...veiculo,
        renavam: veiculo.renavam || fallbackRenavam,
      };
    }
  }

  const mensagem = extrairMensagemErro(resposta);
  if (!mensagem) {
    return null;
  }

  return {
    renavam: fallbackRenavam,
    erro: mensagem,
  };
};

const construirResultadoDosItens = (itens) => {
  const veiculos = itens
    .map((item) => item?.veiculo)
    .filter((item) => item && typeof item === "object");
  return normalizarResultado({ veiculos });
};

const JOB_STATUS_SUCESSO = new Set([
  "sucesso",
  "success",
  "done",
  "completed",
  "finalizado",
  "concluido",
  "concluído",
]);

const JOB_STATUS_ERRO = new Set([
  "erro",
  "error",
  "failed",
  "falha",
  "cancelado",
  "canceled",
  "cancelled",
]);

const derivarStatusPainel = (status) => {
  const valor = ensureString(status).toLowerCase();
  if (!valor || valor === "idle") {
    return "idle";
  }
  if (JOB_STATUS_SUCESSO.has(valor)) {
    return "sucesso";
  }
  if (JOB_STATUS_ERRO.has(valor)) {
    return "erro";
  }
  return "loading";
};

const inicializarItensPorRenavam = (renavams = [], veiculosInfo = []) => {
  const normalizados = Array.isArray(veiculosInfo)
    ? veiculosInfo.map((entrada) => normalizarVeiculoSelecionado(entrada))
    : [];
  const mapaFallback = new Map();
  normalizados.forEach((info) => {
    if (info?.renavam && !mapaFallback.has(info.renavam)) {
      mapaFallback.set(info.renavam, info);
    }
  });
  return renavams.map((renavam, indice) => {
    const renavamNormalizado = normalizarRenavam(renavam);
    const infoIndice = normalizados[indice];
    const veiculoInfo =
      (infoIndice && infoIndice.renavam === renavamNormalizado
        ? infoIndice
        : mapaFallback.get(renavamNormalizado)) || null;
    return {
      jobItemId: null,
      renavam: renavamNormalizado,
      ordem: indice,
      status: "pending",
      mensagem: "Aguardando retorno do DETRAN.",
      erro: null,
      atualizadoEm: null,
      responseBody: null,
      veiculo: veiculoInfo
        ? {
            renavam: renavamNormalizado,
            placa: veiculoInfo.placa || "",
          }
        : null,
      raw: null,
    };
  });
};

const normalizarJobKey = (payload, fallback) =>
  payload?.jobKey || payload?.key || payload?.id || fallback || null;

const montarItemDoJob = (payload, { ordemFallback = 0 } = {}) => {
  if (!payload || typeof payload !== "object") {
    return null;
  }
  const renavam =
    normalizarRenavam(
      payload.renavam ||
        payload.inputRenavam ||
        payload.input?.renavam ||
        payload.request?.renavam ||
        payload.body?.renavam,
    ) || null;
  const responseBody =
    parseResponseBody(payload.responseBody || payload.body || payload.response) || null;
  const veiculo = extrairVeiculoPrincipal(responseBody, renavam);
  if (veiculo && veiculo.renavam) {
    veiculo.renavam = normalizarRenavam(veiculo.renavam);
  }
  const mensagem = extrairMensagemErro(payload) || extrairMensagemErro(responseBody);
  return {
    jobItemId: payload.jobItemId || payload.id || payload.key || payload.jobItemKey || null,
    renavam: normalizarRenavam(veiculo?.renavam || renavam),
    ordem: Number.isFinite(payload.ordem) ? payload.ordem : ordemFallback,
    status: payload.status || "pending",
    mensagem: mensagem || payload.status || "Aguardando atualização.",
    erro: extrairMensagemErro(payload.error ? payload : responseBody) || null,
    atualizadoEm: payload.updatedAt || payload.timestamp || new Date().toISOString(),
    responseBody,
    veiculo: veiculo || null,
    raw: payload,
  };
};

const mesclarItens = (itensAtuais, novoItem) => {
  if (!novoItem) {
    return itensAtuais;
  }
  const lista = Array.isArray(itensAtuais) ? [...itensAtuais] : [];
  const renavamNormalizado = normalizarRenavam(novoItem.renavam);
  const ordemValida = Number.isFinite(novoItem.ordem) ? novoItem.ordem : null;

  const indicePorJobItem =
    novoItem.jobItemId && lista.length
      ? lista.findIndex(
          (item) => item.jobItemId && item.jobItemId === novoItem.jobItemId,
        )
      : -1;

  let indicePorRenavamDisponivel = -1;
  if (renavamNormalizado) {
    indicePorRenavamDisponivel = lista.findIndex((item) => {
      if (normalizarRenavam(item.renavam) !== renavamNormalizado) {
        return false;
      }
      if (!item.jobItemId) {
        return true;
      }
      if (!novoItem.jobItemId) {
        return false;
      }
      return item.jobItemId === novoItem.jobItemId;
    });
  }

  const indicePorOrdem =
    ordemValida !== null
      ? lista.findIndex((item) => Number.isFinite(item.ordem) && item.ordem === ordemValida)
      : -1;

  let indiceRenavamFallback = -1;
  if (renavamNormalizado) {
    indiceRenavamFallback = lista.findIndex(
      (item) => normalizarRenavam(item.renavam) === renavamNormalizado,
    );
  }

  const indiceFinal = [indicePorJobItem, indicePorRenavamDisponivel, indicePorOrdem, indiceRenavamFallback].find(
    (valor) => valor >= 0,
  );

  if (indiceFinal === undefined || indiceFinal === -1) {
    lista.push(novoItem);
    return lista.sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0));
  }
  const anterior = lista[indiceFinal];
  lista[indiceFinal] = {
    ...anterior,
    ...novoItem,
    ordem: Number.isFinite(novoItem.ordem) ? novoItem.ordem : anterior.ordem,
    veiculo: novoItem.veiculo || anterior.veiculo,
    responseBody: novoItem.responseBody || anterior.responseBody,
    erro: novoItem.erro ?? anterior.erro,
    mensagem: novoItem.mensagem || anterior.mensagem,
  };
  return lista.sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0));
};

const localizarIndiceRenavam = (lista, renavam) => {
  if (!Array.isArray(lista) || !renavam) {
    return -1;
  }
  const alvo = normalizarRenavam(renavam);
  return lista.findIndex((valor) => normalizarRenavam(valor) === alvo);
};

const mapearJobResumo = (payload, fallbackKey, fallbackConsultaTipos) => {
  if (!payload) {
    return null;
  }
  const normalizado = normalizeJobUpdate(payload);
  const jobKey = normalizarJobKey(payload, fallbackKey);
  const consultaTiposNormalizados = normalizarConsultaTipos(
    payload?.consultaTipos ||
      payload?.tiposConsulta ||
      normalizado?.consultaTipos ||
      fallbackConsultaTipos,
  );
  return {
    ...normalizado,
    jobKey,
    raw: payload,
    consultaTipos: consultaTiposNormalizados,
  };
};

const atualizarEstadoComJobResumo = (estadoAnterior, payload) => {
  if (!payload) {
    return estadoAnterior;
  }
  const fallbackConsultaTipos =
    estadoAnterior?.jobResumo?.consultaTipos || estadoAnterior?.parametros?.consultaTipos;
  const jobResumo = mapearJobResumo(
    payload,
    estadoAnterior?.jobResumo?.jobKey,
    fallbackConsultaTipos,
  );
  if (!jobResumo) {
    return estadoAnterior;
  }
  const painelStatus = derivarStatusPainel(jobResumo.status);
  const finalizadoAgora = estadoAnterior?.status === "loading" && painelStatus !== "loading";
  const deveAtualizarRecebido =
    painelStatus === "loading" || !estadoAnterior?.recebidoEm || finalizadoAgora;
  const recebidoEm = deveAtualizarRecebido
    ? finalizadoAgora
      ? jobResumo.finishedAt || jobResumo.updatedAt || new Date().toISOString()
      : jobResumo.updatedAt || new Date().toISOString()
    : estadoAnterior.recebidoEm;
  const erroAtualizado =
    painelStatus === "erro"
      ? extrairMensagemErro(payload) || estadoAnterior?.erro
      : estadoAnterior?.erro;
  return {
    ...estadoAnterior,
    jobResumo,
    status: painelStatus,
    recebidoEm,
    erro: erroAtualizado,
    iniciadoEm:
      estadoAnterior?.iniciadoEm ||
      estadoAnterior?.parametros?.iniciadoEm ||
      jobResumo.raw?.startedAt ||
      estadoInicial.iniciadoEm,
  };
};

const atualizarEstadoComItens = (estadoAnterior, itensAtualizados) => {
  const novoResultado = construirResultadoDosItens(itensAtualizados);
  return {
    ...estadoAnterior,
    itens: itensAtualizados,
    resultado: novoResultado,
    recebidoEm: new Date().toISOString(),
    iniciadoEm:
      estadoAnterior?.iniciadoEm ||
      estadoAnterior?.parametros?.iniciadoEm ||
      estadoInicial.iniciadoEm,
  };
};

const obterStatusDebitosJob = async (jobKey) => {
  if (!jobKey) {
    return null;
  }
  const { data } = await frotistaAPI.get(`/debitos/job/${encodeURIComponent(jobKey)}`);
  return data;
};

const extrairItensSnapshot = (snapshot) => {
  if (!snapshot) {
    return [];
  }
  if (Array.isArray(snapshot.items)) {
    return snapshot.items;
  }
  if (Array.isArray(snapshot.itens)) {
    return snapshot.itens;
  }
  return [];
};

const carregarEstadoPersistido = () => {
  if (typeof window === "undefined") {
    return estadoInicial;
  }
  try {
    const salvo = window.sessionStorage.getItem(STORAGE_KEY);
    if (!salvo) {
      return estadoInicial;
    }
    const parsed = JSON.parse(salvo);
    return {
      ...estadoInicial,
      ...parsed,
      resultado: parsed?.resultado || estadoInicial.resultado,
      itens: Array.isArray(parsed?.itens) ? parsed.itens : estadoInicial.itens,
    };
  } catch (erro) {
    console.warn("Falha ao carregar estado de débitos em lote.", erro);
    return estadoInicial;
  }
};

function DebitosLoteProvider({ children }) {
  const [estado, setEstado] = useState(carregarEstadoPersistido);
  const [painelAberto, setPainelAberto] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const monitorandoJobRef = useRef(null);
  const ultimoToastStatusRef = useRef(estado.status);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(estado));
    } catch (erro) {
      console.warn("Falha ao persistir estado de débitos em lote.", erro);
    }
  }, [estado]);

  const handleJobUpdate = useCallback((payload) => {
    if (!payload) {
      return;
    }
    setEstado((prev) => atualizarEstadoComJobResumo(prev, payload));
  }, []);

  const handleJobItem = useCallback((payload) => {
    if (!payload) {
      return;
    }
    setEstado((prev) => {
      const veiculosSelecionados = extrairVeiculosSelecionados(prev?.parametros);
      const listaRenavams = veiculosSelecionados.map((item) => item.renavam);
      const indiceRenavam = localizarIndiceRenavam(listaRenavams, payload?.renavam);
      const itensBase =
        Array.isArray(prev.itens) && prev.itens.length
          ? prev.itens
          : inicializarItensPorRenavam(listaRenavams, veiculosSelecionados);
      const itemNormalizado = montarItemDoJob(payload, {
        ordemFallback: indiceRenavam >= 0 ? indiceRenavam : itensBase.length,
      });
      const itensAtualizados = mesclarItens(itensBase, itemNormalizado);
      return atualizarEstadoComItens(prev, itensAtualizados);
    });
  }, []);

  const { start: iniciarMonitoramento, stop: pararMonitoramento } = useLoteJobSubscription({
    onJobUpdate: handleJobUpdate,
    onJobItem: handleJobItem,
    fetchStatus: obterStatusDebitosJob,
  });

  const abrirPainel = useCallback(() => setPainelAberto(true), []);
  const fecharPainel = useCallback(() => setPainelAberto(false), []);

  const limparConsulta = useCallback(() => {
    monitorandoJobRef.current = null;
    pararMonitoramento();
    setEstado(estadoInicial);
  }, [pararMonitoramento]);

  const consultarDebitosLote = useCallback(
    async ({
      veiculos: veiculosEntrada,
      renavams,
      somenteAnterior = false,
      somenteMulta = false,
      filtroMulta = 0,
      tiposConsulta = null,
    }) => {
      const listaVeiculos = extrairVeiculosSelecionados({
        veiculos: veiculosEntrada,
        renavams,
      });
      const listaRenavams = listaVeiculos.map((item) => item.renavam);

      if (listaRenavams.length === 0) {
        const mensagem = "Selecione ao menos um veículo com RENAVAM para consultar.";
        toast.error(mensagem);
        throw new Error(mensagem);
      }

      const consultaTiposSelecionados = normalizarConsultaTipos(
        tiposConsulta || estado?.parametros?.consultaTipos,
      );
      const tiposConsultaPayload = consultaTiposToArray(consultaTiposSelecionados);
      const payloadEnvio = {
        renavams: listaRenavams,
        veiculos: listaVeiculos,
        somenteAnterior: Boolean(somenteAnterior),
        somenteMulta: Boolean(somenteMulta),
        filtroMulta: Number.isFinite(Number(filtroMulta)) ? Number(filtroMulta) : 0,
        tiposConsulta: tiposConsultaPayload,
      };
      const inicioConsulta = new Date().toISOString();
      const parametrosComInicio = {
        ...payloadEnvio,
        consultaTipos: consultaTiposSelecionados,
        iniciadoEm: inicioConsulta,
      };

      setCarregando(true);
      const itensIniciais = inicializarItensPorRenavam(listaRenavams, listaVeiculos);
      setEstado({
        ...estadoInicial,
        status: "loading",
        parametros: parametrosComInicio,
        itens: itensIniciais,
        resultado: construirResultadoDosItens(itensIniciais),
        recebidoEm: inicioConsulta,
        iniciadoEm: inicioConsulta,
      });
      abrirPainel();
      monitorandoJobRef.current = null;
      pararMonitoramento();

      try {
        const { data } = await frotistaAPI.post("/debitos", payloadEnvio);
        const jobPayload = { ...(data?.job || data || {}) };
        jobPayload.jobKey =
          jobPayload.jobKey ||
          data?.jobKey ||
          jobPayload.jobId ||
          jobPayload.id ||
          jobPayload.key ||
          null;
        const jobResumo = mapearJobResumo(jobPayload, jobPayload.jobKey, consultaTiposSelecionados);
        const jobKey = jobResumo?.jobKey;

        if (!jobKey) {
          throw new Error("O backend não retornou o identificador do job.");
        }

        setEstado((prev) => {
          const itensBase =
            Array.isArray(prev.itens) && prev.itens.length
              ? prev.itens
              : inicializarItensPorRenavam(listaRenavams, listaVeiculos);
          const jobItemsOrigem = Array.isArray(jobPayload?.items)
            ? jobPayload.items
            : Array.isArray(jobPayload?.itens)
              ? jobPayload.itens
              : [];
          const itensJob = jobItemsOrigem.reduce((acumulado, item, index) => {
            const indiceRenavam = localizarIndiceRenavam(listaRenavams, item?.renavam);
            const fallbackIndice = indiceRenavam >= 0 ? indiceRenavam : index;
            return mesclarItens(
              acumulado,
              montarItemDoJob(item, { ordemFallback: fallbackIndice }),
            );
          }, itensBase);
          const estadoComResumo = atualizarEstadoComJobResumo(
            {
              ...prev,
              parametros: parametrosComInicio,
            },
            jobPayload,
          );
          return atualizarEstadoComItens(estadoComResumo, itensJob);
        });

        toast.success("Consulta de débitos em lote iniciada. Acompanhe no painel global.");
        monitorandoJobRef.current = jobKey;
        iniciarMonitoramento(jobKey);
        return jobResumo;
      } catch (erro) {
        const mensagem =
          erro?.response?.data?.message ||
          erro?.message ||
          "Não foi possível iniciar a consulta de débitos em lote.";
        setEstado({
          status: "erro",
          parametros: parametrosComInicio,
          resultado: estadoInicial.resultado,
          erro: mensagem,
          recebidoEm: new Date().toISOString(),
          itens: inicializarItensPorRenavam(listaRenavams, listaVeiculos),
          jobResumo: null,
        });
        toast.error(mensagem);
        monitorandoJobRef.current = null;
        throw new Error(mensagem);
      } finally {
        setCarregando(false);
      }
    },
    [abrirPainel, estado?.parametros?.consultaTipos, iniciarMonitoramento, pararMonitoramento],
  );

  const aplicarSnapshotJob = useCallback((snapshot) => {
    if (!snapshot) {
      return;
    }
    setEstado((prev) => {
      let atualizado = prev;
      const jobPayload = snapshot.job || snapshot;
      if (jobPayload) {
        atualizado = atualizarEstadoComJobResumo(atualizado, jobPayload);
      }
      const itensSnapshot = extrairItensSnapshot(snapshot);
      if (!itensSnapshot.length) {
        return atualizado;
      }
      const veiculosSelecionados = extrairVeiculosSelecionados(atualizado?.parametros);
      const listaRenavams = veiculosSelecionados.map((item) => item.renavam);
      const itensBase =
        Array.isArray(atualizado.itens) && atualizado.itens.length
          ? atualizado.itens
          : inicializarItensPorRenavam(listaRenavams, veiculosSelecionados);
      const itensAtualizados = itensSnapshot.reduce((acumulado, item, index) => {
        const indiceRenavam = localizarIndiceRenavam(listaRenavams, item?.renavam);
        const fallbackIndice = indiceRenavam >= 0 ? indiceRenavam : index;
        return mesclarItens(
          acumulado,
          montarItemDoJob(item, { ordemFallback: fallbackIndice }),
        );
      }, itensBase);
      return atualizarEstadoComItens(atualizado, itensAtualizados);
    });
  }, []);

  const sincronizarJobAtual = useCallback(
    async (jobKey) => {
      try {
        const snapshot = await obterStatusDebitosJob(jobKey);
        if (snapshot) {
          aplicarSnapshotJob(snapshot);
        }
      } catch (_erro) {
        // Ignora falhas de sincronização
      }
    },
    [aplicarSnapshotJob],
  );

  useEffect(() => {
    const jobKey = estado?.jobResumo?.jobKey;
    let snapshotInterval;
    if (estado.status === "loading" && jobKey) {
      if (monitorandoJobRef.current !== jobKey) {
        monitorandoJobRef.current = jobKey;
        iniciarMonitoramento(jobKey);
        sincronizarJobAtual(jobKey);
      }
      snapshotInterval = setInterval(() => {
        if (monitorandoJobRef.current === jobKey && estado.status === "loading") {
          sincronizarJobAtual(jobKey);
        }
      }, 15000);
    } else if (monitorandoJobRef.current && estado.status !== "loading") {
      pararMonitoramento();
      monitorandoJobRef.current = null;
    }
    return () => {
      if (snapshotInterval) {
        clearInterval(snapshotInterval);
      }
    };
  }, [
    estado.jobResumo?.jobKey,
    estado.status,
    iniciarMonitoramento,
    pararMonitoramento,
    sincronizarJobAtual,
  ]);

  useEffect(() => {
    if (estado.status === "sucesso" && ultimoToastStatusRef.current !== "sucesso") {
      toast.success("Consulta de débitos em lote concluída.");
    } else if (
      estado.status === "erro" &&
      estado.jobResumo &&
      ultimoToastStatusRef.current !== "erro"
    ) {
      const mensagem = estado.erro || "Não foi possível concluir a consulta de débitos em lote.";
      toast.error(mensagem);
    }
    ultimoToastStatusRef.current = estado.status;
  }, [estado.erro, estado.jobResumo, estado.status]);

  const resumo = useMemo(() => {
    if (!estado?.resultado) {
      return { totalVeiculos: 0, totalDebitos: 0, totalMultas: 0 };
    }
    return calcularResumo(estado.resultado);
  }, [estado]);

  const temConsulta = useMemo(() => {
    if (estado.status !== "idle") {
      return true;
    }
    return normalizarVeiculos(estado?.resultado).length > 0;
  }, [estado]);

  const valorContexto = useMemo(
    () => ({
      status: estado.status,
      consulta: estado,
      resumo,
      carregando,
      painelAberto,
      abrirPainel,
      fecharPainel,
      limparConsulta,
      consultarDebitosLote,
      temConsulta,
    }),
    [abrirPainel, carregando, estado, fecharPainel, limparConsulta, painelAberto, resumo, temConsulta, consultarDebitosLote],
  );

  return (
    <DebitosLoteContext.Provider value={valorContexto}>
      {children}
      <DebitosLotePanel
        aberto={painelAberto}
        onFechar={fecharPainel}
        estado={estado}
        carregando={carregando}
        onLimpar={limparConsulta}
        resumo={resumo}
      />
    </DebitosLoteContext.Provider>
  );
}

const useDebitosLote = () => {
  const contexto = useContext(DebitosLoteContext);
  if (!contexto) {
    throw new Error("useDebitosLote deve ser usado dentro de DebitosLoteProvider");
  }
  return contexto;
};

export { DebitosLoteProvider, useDebitosLote };
